import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

const SCRIPT_URL = "https://www.google.com/recaptcha/api.js?render=explicit";

let scriptPromise = null;

const loadRecaptcha = () => {
  if (window.grecaptcha?.render) return Promise.resolve(window.grecaptcha);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.onload = () => window.grecaptcha.ready(() => resolve(window.grecaptcha));
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error("reCAPTCHA failed to load"));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

export const GoogleRecaptcha = forwardRef(({ onChange }, ref) => {
  const containerRef = useRef(null);
  const widgetId = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetId.current !== null) window.grecaptcha?.reset(widgetId.current);
    },
  }));

  useEffect(() => {
    let cancelled = false;
    loadRecaptcha()
      .then((grecaptcha) => {
        if (cancelled || !containerRef.current || widgetId.current !== null) return;
        widgetId.current = grecaptcha.render(containerRef.current, {
          sitekey: process.env.REACT_APP_RECAPTCHA_SITE_KEY,
          callback: (token) => onChangeRef.current?.(token),
          "expired-callback": () => onChangeRef.current?.(""),
          "error-callback": () => onChangeRef.current?.(""),
        });
      })
      .catch((error) => console.log(error.message));
    return () => {
      cancelled = true;
    };
  }, []);

  return <div ref={containerRef} />;
});
